import Popup from "../../../hoc/Popup/Popup";
import {
  capitalizeWords,
  formatDateToSwedish,
  formatPersonalNumber,
  formatPhoneNumber,
} from "../../../shared/utility";
import {
  IBooking,
  ICancelBookingInputType,
  ICoach,
  ICourt,
  ICreateGuest,
  IEventPrice,
  IGuest,
  IPermission,
  IPrices,
  ISummaryItem,
  IUser,
} from "../../../types/api";
import "./AdminHandleBookingPopup.scss";
import MemberIdTag from "../../../components/UI/MemberIdTag/MemberIdTag";
import { useCallback, useEffect, useRef, useState } from "react";
import { getUserByMemberId } from "../../../api/users/users";
import {
  AdminCheckInBooking,
  AdminReportNoShow,
  cancelBooking,
  updateBookingComment,
} from "../../../api/bookings/bookings";
import HandleConfirmationPopup from "../../HandleConfirmation/HandleConfirmationPopup";
import ConfirmationPopup from "../../../hoc/ConfirmationPopup/ConfirmationPopup";
import { Id, toast } from "react-toastify";
import {
  createGuest,
  getGuestByMemberId,
  searchGuests,
} from "../../../api/guests/guests";
import {
  emailRegex,
  firstNameRegex,
  lastNameRegex,
  phoneRegex,
  socialSecurityRegex,
} from "../../../shared/regex";
import EditCoPlayersPopup from "../../Bookings/EditCoPlayersPopup/EditCoPlayersPopup";
import { BookingEnum, StatusEnum } from "../../../shared/enums";
import { getCoachByID } from "../../../api/coaches/coaches";
import { formatDate } from "../../../shared/dateUtils";
import ChooseUserPopup from "../ChooseUserPopup/ChooseUserPopup";
import singleCross from "../../../assets/images/single-corss.svg";
import EditMainBookerPopup from "../../Bookings/EditMainBookerPopup/EditMainBookerPopup";
interface IAdminHandleBookingPopupProps {
  onClose: () => void;
  showPopup: boolean;
  slot: ISummaryItem;
  courts: ICourt[];
  onConfirm: () => void;
  coaches: ICoach[];
  isSemiNoShow?: boolean;
  filterDate: Date;
  onRefetchComment: () => void;
  prices: IPrices[];
  permissions: IPermission[];
}

const statusTexts: Partial<{ [key in StatusEnum]: string }> = {
  [StatusEnum.CHECKED_IN]: "Markerar som incheckad...",
  [StatusEnum.CONFIRMED]: "Markerar som icke incheckad...",
  [StatusEnum.PAID]: "Markerar som betald...",
};

function AdminHandleBookingPopup(props: IAdminHandleBookingPopupProps) {
  const { slot, showPopup } = props;
  const [cancelDueToRain, setCancelDueToRain] = useState<boolean>(false);
  const [addGuestData, setAddGuestData] = useState<ICreateGuest>({
    first_name: "",
    last_name: "",
    social_security_number: "",
    times_played_allowed: 10,
    family_member_id: null,
    phone_number: "",
    email: "",
  });

  const [showChooseUserPopup, setShowChooseUserPopup] =
    useState<boolean>(false);
  const [bookAsFamilyMember, setBookAsFamilyMember] = useState(false);
  const [comment, setComment] = useState<string>("");
  const [showEditCoPlayersPopup, setShowEditCoPlayersPopup] =
    useState<boolean>(false);
  const [showEditMainBookerPopup, setShowEditMainBookerPopup] =
    useState<boolean>(false);
  const [showMarkAsNoShowPopup, setShowMarkAsNoShowPopup] =
    useState<boolean>(false);
  const [showDeleteBookingPopup, setShowDeleteBookingPopup] =
    useState<boolean>(false);
  const [mainBookerData, setMainBookerData] = useState<IUser>();
  const [coPlayersData, setCoPlayersData] = useState<IUser[]>();
  const [guestsData, setGuestsData] = useState<IGuest[]>();
  const [coachData, setCoachData] = useState<ICoach>();
  const [changedGuestFlag, setChangedGuestFlag] = useState<boolean>(false);
  const [checkedInBookingCompare, setCheckedInBookingCompare] =
    useState<IBooking>();
  const [updatedBookingCompare, setUpdatedBookingCompare] =
    useState<IBooking>();
  const [loading, setLoading] = useState<boolean>(false);
  const [guestSelection, setGuestSelection] = useState<"existing" | "new">(
    "existing",
  );
  const [editCommentToggle, setEditCommentToggle] = useState<boolean>(false);
  const [searchString, setSearchString] = useState<string>("");
  const [searchedGuests, setSearchedGuests] = useState<IGuest[]>([]);
  const [sendEmail, setSendEmail] = useState<boolean>(true);

  const handleClose = () => {
    setCheckedInBookingCompare(undefined);
    setShowMarkAsNoShowPopup(false);
    setEditCommentToggle(false);
    setGuestsData([]);
    setSearchString("");
    props.onClose();
  };
  // Which guest is being assigned when we need the admin to pick a slot.
  const [guestSlotChoice, setGuestSlotChoice] = useState<{
    guestId: string;
    guestName: string;
    paidIndex: number;
    unpaidIndex: number;
  } | null>(null);

  /**
   * Assign a guest to a free slot.
   *
   * If some free slots were paid for up front (PAID_NOT_CHOSEN) and others
   * weren't, we can't guess which one this guest belongs to — ask the admin.
   */
  const handleAssignGuest = (guestId: string, guestName: string) => {
    const guests = bookingToLoop?.guests || [];
    const freeIndexes = guests
      .map((g, i) => ({ g, i }))
      .filter(({ g }) => g.member_id === "guest");

    const paid = freeIndexes.filter(
      ({ g }) => g.status === StatusEnum.PAID_NOT_CHOSEN,
    );
    const unpaid = freeIndexes.filter(
      ({ g }) => g.status !== StatusEnum.PAID_NOT_CHOSEN,
    );

    if (paid.length > 0 && unpaid.length > 0) {
      setGuestSlotChoice({
        guestId,
        guestName,
        paidIndex: paid[0].i,
        unpaidIndex: unpaid[0].i,
      });
      return;
    }

    const target = paid[0] ?? unpaid[0];
    if (!target) return;

    assignGuestToSlot(guestId, target.i, paid.length > 0);
  };

  const assignGuestToSlot = (
    guestId: string,
    guestIndex: number,
    slotWasPaid: boolean,
  ) => {
    // A slot that was already paid for keeps its payment — the guest is simply
    // not checked in yet.
    const status = slotWasPaid
      ? StatusEnum.PAID_NOT_CHECKED_IN
      : StatusEnum.CONFIRMED;
    handleStatusChange(
      guestId,
      "guest",
      status,
      slotWasPaid
        ? "Tilldelar betald gästplats..."
        : (statusTexts[StatusEnum.CONFIRMED] as string),
      bookAsFamilyMember ?? false,
      guestIndex,
    );
  };

  const handleStatusChange = async (
    member_id: string,
    type: "guest" | "co_player" | "member",
    status: StatusEnum,
    statusText: string,
    family_member: boolean = bookAsFamilyMember ?? false,
    guest_index?: number,
  ) => {
    if (!member_id) return;
    const booking_id = slot.booking[0].booking_id;
    const date = booking.datetime_start;

    let loadingToast = toast.loading(statusText);
    setLoading(true);
    try {
      const response = await AdminCheckInBooking(
        booking_id,
        date,
        member_id,
        type,
        status,
        family_member,
        guest_index,
      );
      if (response && "message" in response === false) {
        toast.success("Status uppdaterad");
        setCheckedInBookingCompare(response);

        if (type === "guest") {
          setChangedGuestFlag(!changedGuestFlag);
        }

        props.onConfirm();
        toast.dismiss(loadingToast);
        setLoading(false);
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      setLoading(false);
    }
  };

  const handleCreateGuest = async () => {
    if (
      addGuestData.first_name.length < 2 ||
      addGuestData.last_name.length < 2 ||
      addGuestData.social_security_number.length < 11
    ) {
      toast.error("Fyll i alla fält korrekt");
      return;
    }
    if (bookAsFamilyMember) {
      addGuestData.times_played_allowed = 18;
    } else {
      addGuestData.times_played_allowed = 10;
    }

    // Captured before the form is cleared, since the slot-choice popup names
    // the guest it is asking about.
    const guestName = `${capitalizeWords(
      addGuestData.first_name,
    )} ${capitalizeWords(addGuestData.last_name)}`.trim();

    try {
      const response = await createGuest(addGuestData);
      if (response && "message" in response === false) {
        toast.success("Gäst skapad");
        setAddGuestData({
          first_name: "",
          last_name: "",
          social_security_number: "",
          times_played_allowed: 10,
          family_member_id: null,
          phone_number: "",
          email: "",
        });
        // Goes through the same assignment path as picking an existing guest.
        // It used to call handleStatusChange directly with CONFIRMED and no
        // slot index, so a newly created guest silently took an unpaid slot
        // even when a pre-paid one was free, and was never asked about.
        handleAssignGuest(response.member_id, guestName);
      }
    } catch (error: any) {
      toast.error(error);
    }
  };
  const handleMarkAsNoShow = async () => {
    setLoading(true);
    const booking_id = slot.booking[0].booking_id;
    const date = slot.booking[0].datetime_start;
    try {
      const response = await AdminReportNoShow(booking_id, date);
      if (response && "message" in response === false) {
        setCheckedInBookingCompare(response);
        props.onConfirm();
        handleClose();
      }
    } catch (error: any) {
      toast.error(error);
    } finally {
      setLoading(false);
    }
  };
  const handleCommentBooking = async () => {
    try {
      const response = await updateBookingComment(
        comment,
        bookingToLoop.court_id,
        formatDate(props.filterDate, "yyyy-MM-dd"),
        bookingToLoop.slot_index,
      );
      if (response && "message" in response === false) {
        props.onRefetchComment();
        setCheckedInBookingCompare(response);
        setEditCommentToggle(false);
        toast.success("Kommentar sparad");
      }
    } catch (error: any) {
      toast.error(error);
    }
  };

  const handleAddGuestKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };
  const handleGuestInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    key:
      | "first_name"
      | "last_name"
      | "social_security_number"
      | "family_member_id"
      | "phone_number"
      | "email",
  ) => {
    let value = e.target.value;
    if (key === "social_security_number") {
      value = formatPersonalNumber(value);
    }
    if (key === "phone_number") {
      value = formatPhoneNumber(value);
    }
    if (key === "family_member_id") {
      if (value === "") {
        setAddGuestData({ ...addGuestData, [key]: null });
      }
      return;
    }
    setAddGuestData({ ...addGuestData, [key]: value });
  };

  const handleToggleGuestSelection = (
    e: any,
    selection: "existing" | "new",
  ) => {
    e.preventDefault();
    setGuestSelection(selection);
  };

  useEffect(() => {
    if (showPopup) {
      handleFetchGuestsData();
    }
  }, [changedGuestFlag]);

  useEffect(() => {
    if (updatedBookingCompare) {
      //write a functi
      const updateCoPlayersData = () => {
        if (
          !updatedBookingCompare ||
          !coPlayersData ||
          !checkedInBookingCompare
        )
          return;

        const currentCoPlayers = updatedBookingCompare.co_players.map(
          (player) => player.member_id,
        );
        const previousCoPlayers = checkedInBookingCompare.co_players.map(
          (player) => player.member_id,
        );

        const addedPlayers = currentCoPlayers.filter(
          (id) => !previousCoPlayers.includes(id),
        );
        const removedPlayers = previousCoPlayers.filter(
          (id) => !currentCoPlayers.includes(id),
        );

        if (addedPlayers.length > 0) {
          addedPlayers.forEach(async (id) => {
            const newPlayer = await getUserByMemberId(id);
            setCoPlayersData((prev) => [...(prev || []), newPlayer]);
          });
        }

        if (removedPlayers.length > 0) {
          setCoPlayersData((prev) =>
            prev?.filter(
              (player) => !removedPlayers.includes(player.member_id),
            ),
          );
        }

        setCheckedInBookingCompare(updatedBookingCompare);
      };

      updateCoPlayersData();
      const updateCoachData = () => {
        if (!updatedBookingCompare || !coachData || !checkedInBookingCompare)
          return;

        const currentCoaches = updatedBookingCompare.coach_id;
        const previousCoaches = checkedInBookingCompare.coach_id;

        const addedCoaches = currentCoaches.filter(
          (id) => !previousCoaches.includes(id),
        );
        const removedCoaches = previousCoaches.filter(
          (id) => !currentCoaches.includes(id),
        );

        if (addedCoaches.length > 0) {
          addedCoaches.forEach(async (id) => {
            const newCoach = await getCoachByID(id);
            setCoachData(newCoach);
          });
        }

        if (removedCoaches.length > 0) {
          setCoachData(undefined);
        }
      };
      updateCoachData();
    }
  }, [updatedBookingCompare]);
  const handleCancelBooking = async (booking: IBooking) => {
    const coachToCancle = booking.coach_id.length > 0 ? booking.coach_id : [];
    const bookingToCancel: ICancelBookingInputType = {
      court_id: booking.court_id,
      datetime_start: booking.datetime_start,
      booking_id: booking.booking_id,
      slot_index: booking.slot_index,
      coach_id: coachToCancle,
    };
    let loadToast = "" as Id;
    setLoading(true);
    try {
      loadToast = toast.loading("Avbokar bokning...");
      const cancelBookingResponse = await cancelBooking(
        bookingToCancel,
        "admin",
        false,
        cancelDueToRain,
        sendEmail,
      );
      if (
        cancelBookingResponse &&
        "message" in cancelBookingResponse === false
      ) {
        toast.success("Bokning avbokad");
        toast.dismiss(loadToast);
        props.onConfirm();
        handleClose();
        setShowDeleteBookingPopup(false);
      }
      toast.dismiss(loadToast);
    } catch (error: any) {
      toast.dismiss(loadToast);
      toast.error("Error cancelling booking" + error);
      setShowDeleteBookingPopup(false);
    } finally {
      setLoading(false);
    }
  };
  const handleFetchMainBookerData = async () => {
    const mainBooker = slot.booking[0].member_id;
    const response = await getUserByMemberId(mainBooker);
    setMainBookerData(response);
  };
  const handleFetchGuestsData = async () => {
    const guestList: IGuest[] = [];

    const guests = bookingToLoop.guests;

    const response = await Promise.all(
      guests.map(async (guest) => {
        if (guest.member_id !== "guest") {
          return await getGuestByMemberId(guest.member_id);
        }
      }),
    );
    response.forEach((res) => {
      if (res) {
        guestList.push(res);
      }
    });
    setGuestsData(guestList);
  };

  const handleFetchCoPlayersData = async () => {
    const coPlayers = slot.booking[0].co_players;
    const response = await Promise.all(
      coPlayers.map(async (coPlayer) => {
        return await getUserByMemberId(coPlayer.member_id);
      }),
    );
    setCoPlayersData(response);
  };

  const handleFetchCoachData = async () => {
    const coach = slot.booking[0].coach_id[0];
    const response = await getCoachByID(coach);
    setCoachData(response);
  };

  const lastWarningTimeRef = useRef<number>(0);

  const showWarning = useCallback(() => {
    const now = Date.now();
    if (now - lastWarningTimeRef.current >= 5000) {
      lastWarningTimeRef.current = now;
      toast.warning("Inga användare hittades");
    }
  }, []);

  const latestGuestSearchRef = useRef<string>("");

  const fetchSearchGuests = async (search: string) => {
    latestGuestSearchRef.current = search;
    try {
      const response = await searchGuests(search);
      if (latestGuestSearchRef.current !== search) {
        return;
      }
      if (response.length === 0) {
        showWarning();
      }
      setSearchedGuests(response);
    } catch (error: any) {
      toast.error(error);
    }
  };
  useEffect(() => {
    if (searchString.length < 3) {
      setSearchedGuests([]);
      return;
    }
    handleSearchGuests();
  }, [searchString]);

  const handleSearchGuests = () => {
    fetchSearchGuests(searchString);
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearchGuests();
    }
  };
  useEffect(() => {
    if (showPopup === false) {
      setCheckedInBookingCompare(undefined);
    }
    if (showPopup === true) {
      console.log(bookingToLoop);
      if (
        booking &&
        booking.booking_id !== checkedInBookingCompare?.booking_id
      ) {
        setComment(booking.comment ? booking.comment : "");
        setCheckedInBookingCompare(booking);
      }
    }
    if (slot) {
      handleFetchMainBookerData();
      handleFetchCoPlayersData();
      handleFetchGuestsData();
      if (slot.booking[0].coach_id.length > 0) {
        handleFetchCoachData();
      }
    }
  }, [showPopup]);

  if (!slot || !slot.booking || slot.booking.length === 0) {
    return null;
  }
  const booking = slot.booking[0] as IBooking;
  const startDate = new Date(booking?.datetime_start || "");
  const { weekday, dateAndMonth, hour } = formatDateToSwedish(
    new Date(startDate),
  );

  function accumulatePrices(finalPrices: IEventPrice[]): string {
    const total = finalPrices.reduce((sum, item) => sum + item.price, 0);
    return total.toString();
  }
  const dateString = `${weekday} ${dateAndMonth}`;
  const courtName = props.courts.find(
    (c) => c.court_id === booking?.court_id,
  )?.name;
  const timeCourtString = `${slot.start_time.slice(
    0,
    5,
  )} - ${slot.end_time.slice(0, 5)} ${courtName}`;
  const userString = `${
    capitalizeWords(mainBookerData?.first_name || "Loading") +
    " " +
    capitalizeWords(mainBookerData?.last_name || "Loading")
  }`;
  const bookingToLoop = checkedInBookingCompare || booking;
  const coachPrize =
    bookingToLoop.coach_id.length > 0
      ? bookingToLoop.final_price.find((event) =>
          event.event.includes("coach_price"),
        )?.price
      : 0;

  const courtType = props.courts.find(
    (court) => court.court_id === booking.court_id,
  )?.court_type;

  const handleCheckIn = (
    userId: string,
    userType: "member" | "co_player" | "guest",
    userStatus: StatusEnum,
    price: number,
    handleStatusChange: (
      member_id: string,
      type: "guest" | "co_player" | "member",
      status: StatusEnum,
      statusText: string,
    ) => void,
  ) => {
    switch (userStatus) {
      case StatusEnum.PAID_NOT_CHECKED_IN:
        handleStatusChange(
          userId,
          userType,
          StatusEnum.PAID,
          "Markerar som fullt betald...",
        );
        break;
      case StatusEnum.CONFIRMED:
        if (price === 0) {
          handleStatusChange(
            userId,
            userType,
            StatusEnum.PAID,
            "Markerar som betald...",
          );
        } else {
          handleStatusChange(
            userId,
            userType,
            StatusEnum.CHECKED_IN,
            "Markerar som incheckad...",
          );
        }
        break;
      case StatusEnum.CHECKED_IN:
        handleStatusChange(
          userId,
          userType,
          StatusEnum.CONFIRMED,
          "Återgår till ej incheckad...",
        );
        break;
      case StatusEnum.PAID:
        handleStatusChange(
          userId,
          userType,
          StatusEnum.CONFIRMED,
          "Återgår till ej incheckad...",
        );
        break;
    }
  };

  const handlePayment = (
    userId: string,
    userType: "member" | "co_player" | "guest",
    userStatus: StatusEnum,
    handleStatusChange: (
      member_id: string,
      type: "guest" | "co_player" | "member",
      status: StatusEnum,
      statusText: string,
    ) => void,
  ) => {
    switch (userStatus) {
      case StatusEnum.CHECKED_IN:
        handleStatusChange(
          userId,
          userType,
          StatusEnum.PAID,
          "Markerar som fullt betald...",
        );

        break;
      case StatusEnum.PAID_NOT_CHECKED_IN:
        handleStatusChange(
          userId,
          userType,
          StatusEnum.CONFIRMED,
          "Återgår till ej betald...",
        );
        break;
      case StatusEnum.PAID:
        handleStatusChange(
          userId,
          userType,
          StatusEnum.CHECKED_IN,
          "Återgår till incheckad...",
        );
        break;
      case StatusEnum.CONFIRMED:
        handleStatusChange(
          userId,
          userType,
          StatusEnum.PAID_NOT_CHECKED_IN,
          "Markerar som betald men ej incheckad...",
        );
        break;
    }
  };

  const renderStatusActions = (
    userId: string,
    userType: "member" | "co_player" | "guest",
    userStatus: StatusEnum,
    price: number,
    handleStatusChange: (
      member_id: string,
      type: "guest" | "co_player" | "member",
      status: StatusEnum,
      statusText: string,
      family_member?: boolean,
      guest_index?: number,
    ) => void,
    guestIndex?: number,
  ) => {
    const isPaid = userStatus === StatusEnum.PAID;
    const isPaidNotCheckedIn = userStatus === StatusEnum.PAID_NOT_CHECKED_IN;
    const isCheckedIn = userStatus === StatusEnum.CHECKED_IN;
    const hasPrice = price > 0;

    // An unassigned guest slot: payment can be registered before we know who
    // the guest is, but there is nobody to check in yet.
    const isUnassignedGuest = userType === "guest" && userId === "guest";
    const isPaidNotChosen = userStatus === StatusEnum.PAID_NOT_CHOSEN;
    const shouldShowActions = !isUnassignedGuest;

    const toggleUnassignedGuestPaid = () =>
      handleStatusChange(
        "guest",
        "guest",
        isPaidNotChosen ? StatusEnum.CONFIRMED : StatusEnum.PAID_NOT_CHOSEN,
        isPaidNotChosen
          ? "Återgår till ej betald..."
          : "Markerar gästplats som betald...",
        false,
        guestIndex,
      );

    return (
      <div className="checkin-text-wrapper">
        <span className="price-for-member">{price} kr</span>

        {isUnassignedGuest && hasPrice && (
          <span
            className={isPaidNotChosen ? "checked-in pointer" : "pointer"}
            onClick={toggleUnassignedGuestPaid}
          >
            {isPaidNotChosen ? (
              "Betald"
            ) : (
              <a className="pointer">Betald</a>
            )}
          </span>
        )}

        {shouldShowActions && (
          <>
            {isCheckedIn || isPaid ? (
              <span
                className="checked-in pointer"
                onClick={() =>
                  handleCheckIn(
                    userId,
                    userType,
                    userStatus,
                    price,
                    handleStatusChange,
                  )
                }
              >
                Incheckad
              </span>
            ) : (
              <a
                className="pointer"
                onClick={() =>
                  handleCheckIn(
                    userId,
                    userType,
                    userStatus,
                    price,
                    handleStatusChange,
                  )
                }
              >
                Checka in
              </a>
            )}

            {hasPrice &&
              (isPaid || isPaidNotCheckedIn ? (
                <span
                  className="checked-in pointer"
                  onClick={() =>
                    handlePayment(
                      userId,
                      userType,
                      userStatus,
                      handleStatusChange,
                    )
                  }
                >
                  Betald
                </span>
              ) : (
                <a
                  className="pointer"
                  onClick={() =>
                    handlePayment(
                      userId,
                      userType,
                      userStatus,
                      handleStatusChange,
                    )
                  }
                >
                  Betald
                </a>
              ))}
          </>
        )}
      </div>
    );
  };

  return (
    <Popup
      onClose={handleClose}
      showPopup={props.showPopup}
      leftTopElement={
        <span className="text-m">
          Hantera bokning{" "}
          {bookingToLoop?.booking_type === BookingEnum.DROP_IN && "(Drop in)"}
        </span>
      }
    >
      <div className="general-popup-wrapper">
        <form
          className="general-popup-form less-gap"
          onSubmit={(e) => {
            e.preventDefault();
            handleCreateGuest();
          }}
        >
          <div className="general-popup-form-group mb-20">
            <div className="general-popup-form-field">
              <div className="general-popup-flex-between">
                <span className="text-m primary-text">Datum</span>
                <div className="text-m primary-text text-svg-wrapper">
                  <span>{dateString}</span>
                </div>
              </div>
              <div className="general-popup-flex-between">
                <span className="text-m primary-text ">Tid + Bana</span>
                <div className="text-m primary-text text-svg-wrapper">
                  <span>{timeCourtString}</span>
                </div>
              </div>
              <div className="general-popup-flex-between">
                <span className="text-m primary-text">Huvudbokare</span>
                <div className="text-m primary-text text-svg-wrapper">
                  <span>
                    {" "}
                    {userString}{" "}
                    <MemberIdTag
                      memberId={mainBookerData?.member_id}
                      memberType={mainBookerData?.member_type}
                    />
                  </span>
                  <div className={`checkin-text-wrapper`}>
                    {(() => {
                      if (mainBookerData) {
                        const memberStatus = bookingToLoop.member_status;
                        const priceForMainBooker =
                          bookingToLoop.final_price.find((event) =>
                            event.event.includes(mainBookerData.member_id),
                          );
                        const priceString = priceForMainBooker?.price || "0";
                        const hasPrice = priceString !== "0";

                        if (memberStatus === "CHECKED_IN" && hasPrice) {
                          return (
                            <>
                              <span className="price-for-member">
                                {priceString} kr
                              </span>
                              <span
                                className="checked-in pointer"
                                onClick={() => {
                                  if (loading) return;
                                  handleStatusChange(
                                    mainBookerData.member_id,
                                    "member",
                                    StatusEnum.CONFIRMED,
                                    statusTexts[StatusEnum.CONFIRMED] as string,
                                  );
                                }}
                              >
                                Incheckad
                              </span>
                              <a
                                className="pointer"
                                onClick={() => {
                                  if (loading) return;
                                  handleStatusChange(
                                    mainBookerData.member_id,
                                    "member",
                                    StatusEnum.PAID,
                                    statusTexts[StatusEnum.PAID] as string,
                                  );
                                }}
                              >
                                Betald
                              </a>
                            </>
                          );
                        } else if (memberStatus === "CHECKED_IN") {
                          return (
                            <>
                              <span className="price-for-member">
                                {priceString} kr
                              </span>
                              <span
                                className="checked-in pointer"
                                onClick={() => {
                                  if (loading) return;
                                  handleStatusChange(
                                    mainBookerData.member_id,
                                    "member",
                                    StatusEnum.CONFIRMED,
                                    statusTexts[StatusEnum.CONFIRMED] as string,
                                  );
                                }}
                              >
                                Incheckad
                              </span>
                            </>
                          );
                        } else if (memberStatus === "PAID") {
                          return (
                            <>
                              <span className="price-for-member">
                                {priceString} kr
                              </span>
                              <span
                                className="checked-in pointer"
                                onClick={() => {
                                  if (loading) return;
                                  handleStatusChange(
                                    mainBookerData.member_id,
                                    "member",
                                    StatusEnum.CONFIRMED,
                                    statusTexts[StatusEnum.CONFIRMED] as string,
                                  );
                                }}
                              >
                                Incheckad
                              </span>
                              {hasPrice && (
                                <span
                                  className="checked-in pointer"
                                  onClick={() => {
                                    if (loading) return;
                                    handleStatusChange(
                                      mainBookerData.member_id,
                                      "member",
                                      StatusEnum.CHECKED_IN,
                                      statusTexts[
                                        StatusEnum.CHECKED_IN
                                      ] as string,
                                    );
                                  }}
                                >
                                  Betald
                                </span>
                              )}
                            </>
                          );
                        } else {
                          return (
                            <>
                              <span className="price-for-member">
                                {priceString} kr
                              </span>
                              <a
                                className="pointer"
                                onClick={() => {
                                  if (loading) return;
                                  handleStatusChange(
                                    mainBookerData.member_id,
                                    "member",
                                    StatusEnum.CHECKED_IN,
                                    statusTexts[
                                      StatusEnum.CHECKED_IN
                                    ] as string,
                                  );
                                }}
                              >
                                Checka in
                              </a>
                            </>
                          );
                        }
                      } else {
                        return null;
                      }
                    })()}
                  </div>
                </div>
              </div>
              <div className="general-popup-flex-between align-top">
                <span className="text-m primary-text">Medspelare</span>
                <span className="text-m primary-text text-svg-wrapper">
                  <div className="chosen-players">
                    {coPlayersData &&
                      coPlayersData.map((player, index) => {
                        const { first_name, last_name } = player;
                        const playerString = `${capitalizeWords(
                          first_name,
                        )} ${capitalizeWords(last_name)}`;
                        const priceForPlayer = bookingToLoop.final_price.find(
                          (event) => {
                            return event.event.includes(player.member_id);
                          },
                        );
                        const priceString = priceForPlayer?.price || "0";
                        const hasPrice = priceString !== "0";
                        return (
                          <div key={index} className="chosen-player">
                            {playerString}{" "}
                            <MemberIdTag
                              memberId={player.member_id}
                              memberType={player.member_type}
                            />
                            {renderStatusActions(
                              player.member_id,
                              "co_player",
                              bookingToLoop.co_players.find(
                                (coPlayer) =>
                                  coPlayer.member_id === player.member_id,
                              )?.status || StatusEnum.CONFIRMED,
                              bookingToLoop.final_price.find((event) =>
                                event.event.includes(player.member_id),
                              )?.price || 0,
                              handleStatusChange,
                            )}
                          </div>
                        );
                      })}
                  </div>
                </span>
              </div>
              {bookingToLoop.guests.length > 0 && (
                <div className="general-popup-flex-between align-top">
                  <span className="text-m primary-text">Gäster</span>
                  <span className="text-m primary-text text-svg-wrapper">
                    <div className="chosen-players">
                      {bookingToLoop.guests.map((guest, index) => {
                        const priceForPlayer = bookingToLoop.final_price.find(
                          (event) => {
                            return event.event.includes(guest.member_id);
                          },
                        );

                        const priceString = priceForPlayer?.price || 0;
                        const hasPrice = priceString !== 0;

                        let guestToShow = guestsData?.find((g) =>
                          g.member_id !== "guest"
                            ? g.member_id === guest.member_id
                            : false,
                        );
                        let guestTimesPlayed = 0;
                        let guestTimesPlayedAllowed = 10;
                        if (guestToShow) {
                          guestTimesPlayed = guestToShow.times_played || 0;
                          guestTimesPlayedAllowed =
                            guestToShow.times_played_allowed || 10;
                        }

                        return (
                          <div key={index} className="chosen-player">
                            {guestToShow
                              ? capitalizeWords(guestToShow.first_name) +
                                " " +
                                capitalizeWords(guestToShow.last_name) +
                                " (" +
                                guestTimesPlayed +
                                "/" +
                                guestTimesPlayedAllowed +
                                ")"
                              : `Gäst ${index + 1}`}
                            {renderStatusActions(
                              guest.member_id,
                              "guest",
                              guest.status || StatusEnum.CONFIRMED,
                              priceString,
                              handleStatusChange,
                              index,
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </span>
                </div>
              )}

              {booking.coach_id.length > 0 && (
                <div className="general-popup-flex-between align-top">
                  <span className="text-m primary-text">
                    {coachData?.is_hitting_partner
                      ? "Hitting Partner"
                      : "Tränare"}
                  </span>
                  <span className="text-m primary-text">
                    {bookingToLoop.coach_id.map((coach, index) => {
                      const coachString =
                        capitalizeWords(
                          coachData?.first_name + " " + coachData?.last_name,
                        ) +
                        " (" +
                        coach +
                        ")";

                      const coachPrizeExists = coachPrize !== 0;
                      const status = bookingToLoop.member_status;
                      return (
                        <div className="checkin-text-wrapper" key={index}>
                          {coachString} {coachPrize} kr
                          {coachPrizeExists &&
                          status === StatusEnum.CHECKED_IN ? (
                            <>
                              <a
                                className="pointer"
                                onClick={() => {
                                  if (loading) return;
                                  handleStatusChange(
                                    coach,
                                    "member",
                                    StatusEnum.PAID,
                                    statusTexts[StatusEnum.PAID] as string,
                                  );
                                }}
                              >
                                Betald
                              </a>
                            </>
                          ) : coachPrizeExists && status === StatusEnum.PAID ? (
                            <>
                              <span
                                className="checked-in pointer"
                                onClick={() => {
                                  if (loading) return;
                                  handleStatusChange(
                                    coach,
                                    "member",
                                    StatusEnum.CHECKED_IN,
                                    statusTexts[
                                      StatusEnum.CHECKED_IN
                                    ] as string,
                                  );
                                }}
                              >
                                Betald
                              </span>
                            </>
                          ) : (
                            <></>
                          )}
                        </div>
                      );
                    })}
                  </span>
                </div>
              )}
              <div className="general-popup-flex-between align-top">
                {/* <span className="text-m primary-text">Pris</span>
                <span className="text-m primary-text">
                  {accumulatePrices(bookingToLoop.final_price)} kr
                </span> */}
              </div>
              {!props.isSemiNoShow && (
                <>
                  <div className="general-popup-flex-right text-m">
                    <span></span>
                    <a onClick={() => setShowEditCoPlayersPopup(true)}>
                      Redigera bokning
                    </a>
                  </div>
                  <div className="general-popup-flex-right text-m">
                    <span></span>
                    <a onClick={() => setShowEditMainBookerPopup(true)}>
                      Ändra huvudbokare
                    </a>
                  </div>
                </>
              )}

              <div className="general-popup-flex-right">
                {booking.status !== "NO_SHOW" ? (
                  <a
                    className="danger text-m pointer"
                    onClick={() => setShowMarkAsNoShowPopup(true)}
                  >
                    No show
                  </a>
                ) : (
                  <div className="text-danger text-m ">
                    Bokningen är angiven som "No show"
                  </div>
                )}
              </div>
              {!props.isSemiNoShow && (
                <>
                  <div className="general-popup-flex-right">
                    <a
                      className="danger text-m pointer"
                      onClick={() => setShowDeleteBookingPopup(true)}
                    >
                      Avboka
                    </a>
                  </div>
                  <div className="handle-activity-flex-between">
                    <span className="text-m secondary-text">
                      Skicka Email vid avbokning
                    </span>
                    <label className="toggle text-m">
                      <input
                        type="checkbox"
                        name="closed"
                        checked={sendEmail}
                        onChange={(e) => setSendEmail(!sendEmail)}
                      />
                      <span className="slider round"></span>
                    </label>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="general-popup-form-group mb-20">
            <div className="general-popup-form-field">
              <span className="text-m">Kommentar</span>
              <span className="text-m">{bookingToLoop.comment}</span>
              {!editCommentToggle && (
                <div className="general-popup-flex-right text-m">
                  <button
                    type="button"
                    onClick={() => setEditCommentToggle(true)}
                  >
                    {bookingToLoop.comment !== "" ? (
                      <span> Uppdatera kommentar</span>
                    ) : (
                      <span>Lägg till kommentar</span>
                    )}
                  </button>
                </div>
              )}
              {editCommentToggle && (
                <>
                  <input
                    className="text-m"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  ></input>
                  <div className="general-popup-flex-right text-m">
                    <button
                      type="button"
                      onClick={() => handleCommentBooking()}
                    >
                      Spara kommentar
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {bookingToLoop.guests.length > 0 &&
            !props.isSemiNoShow &&
            bookingToLoop.guests.some(
              (guest) => guest.member_id === "guest",
            ) && (
              <div className="general-popup-form-group mb-20">
                <div className="general-popup-form-field">
                  <span className="text-m"> Lägg till gästspelare</span>
                  <div className="admin-handle-guest-selection-type text-m">
                    <div className="admin-handle-guest-selection-buttons text-m">
                      <button
                        className={`toggle-button ${
                          guestSelection === "existing" ? "active" : ""
                        }`}
                        onClick={(e) =>
                          handleToggleGuestSelection(e, "existing")
                        }
                      >
                        Befintlig
                      </button>
                      <button
                        type="button"
                        className={`toggle-button ${
                          guestSelection === "new" ? "active" : ""
                        }`}
                        onClick={(e) => handleToggleGuestSelection(e, "new")}
                      >
                        Ny gäst
                      </button>
                    </div>

                    <div className="toggle-flex-between">
                      <span className="text-m secondary-text">
                        Checka in som familjegäst
                      </span>
                      <label className="toggle text-m">
                        <input
                          type="checkbox"
                          name="coach"
                          checked={bookAsFamilyMember}
                          onChange={(e) =>
                            setBookAsFamilyMember(e.target.checked)
                          }
                        />
                        <span className="slider round"></span>
                      </label>
                    </div>
                  </div>
                  {guestSelection === "existing" ? (
                    <>
                      <div className="existing-guests-wrapper">
                        <div className="search-input-btn-wrapper">
                          <div className="search-input-wrapper">
                            <input
                              className="users-search-input text-m"
                              value={searchString}
                              onChange={(e) => setSearchString(e.target.value)}
                              placeholder="Sök på namn & medlemsnummer"
                              onKeyDown={handleKeyDown}
                            />
                            <i className="fa-regular fa-magnifying-glass search-icon"></i>
                          </div>
                        </div>
                      </div>
                      <div className="searched-guests-wrapper text-m">
                        {searchedGuests.length > 0 &&
                          searchedGuests.map((guest, index) => {
                            if (index > 3) return null;
                            if (
                              bookingToLoop.guests.some(
                                (g) => g.member_id === guest.member_id,
                              )
                            ) {
                              return null;
                            }
                            const guestTimesPlayed = guest.times_played || 0;
                            const guestTimesPlayedAllowed =
                              guest.times_played_allowed || 10;

                            const guestSocialSecurityNumber =
                              guest.social_security_number || "";
                            const hasMaxPlayTimes =
                              guestTimesPlayed >= guestTimesPlayedAllowed;

                            return (
                              <div
                                key={guest.member_id}
                                className="player-wrapper pointer"
                                onClick={() =>
                                  handleAssignGuest(
                                    guest.member_id,
                                    `${capitalizeWords(
                                      guest.first_name,
                                    )} ${capitalizeWords(guest.last_name)}`,
                                  )
                                }
                              >
                                <div
                                  className={`player-name ${
                                    hasMaxPlayTimes ? "frozen" : ""
                                  }`}
                                >
                                  <div className="guest-info-spacer">
                                    <span>
                                      {capitalizeWords(guest.first_name)}{" "}
                                      {capitalizeWords(guest.last_name)}{" "}
                                    </span>
                                    <span>{guestSocialSecurityNumber}</span> 
                                    <span>(
                                    {guestTimesPlayed}/{guestTimesPlayedAllowed}
                                    )</span>
                                  </div>
                                </div>
                                <div className="player-id">
                                  {guest.member_id}
                                </div>
                                <a className="choose-player">Välj gäst</a>
                              </div>
                            );
                          })}
                      </div>
                    </>
                  ) : (
                    <div className="add-guest-wrapper">
                      <div className="add-guest-inputs-wrapper">
                        <input
                          className="text-m"
                          placeholder="Förnamn*"
                          type="text"
                          minLength={2}
                          required
                          pattern={firstNameRegex}
                          onKeyDown={handleAddGuestKeyDown}
                          value={addGuestData.first_name}
                          onChange={(e) =>
                            handleGuestInputChange(e, "first_name")
                          }
                        />
                        <input
                          className="text-m"
                          placeholder="Efternamn*"
                          type="text"
                          minLength={2}
                          pattern={lastNameRegex}
                          required
                          onKeyDown={handleAddGuestKeyDown}
                          value={addGuestData.last_name}
                          onChange={(e) =>
                            handleGuestInputChange(e, "last_name")
                          }
                        />
                        <input
                          className="text-m"
                          placeholder="yymmdd-xxxx*"
                          pattern={socialSecurityRegex}
                          type="text"
                          maxLength={11}
                          onKeyDown={handleAddGuestKeyDown}
                          required
                          value={addGuestData.social_security_number}
                          onChange={(e) =>
                            handleGuestInputChange(e, "social_security_number")
                          }
                        />
                        <input
                          className="text-m"
                          type="phone"
                          placeholder="+46 70-123 4567"
                          pattern={phoneRegex}
                          onKeyDown={handleAddGuestKeyDown}
                          value={addGuestData.phone_number}
                          onChange={(e) =>
                            handleGuestInputChange(e, "phone_number")
                          }
                        />
                        <input
                          className="text-m"
                          placeholder="E-post"
                          type="text"
                          pattern={emailRegex}
                          onKeyDown={handleAddGuestKeyDown}
                          value={addGuestData.email}
                          onChange={(e) => handleGuestInputChange(e, "email")}
                        />
                        <div className="add-guest-button-wrapper">
                          <button className="text-m" type="submit">
                            Lägg till gäst
                          </button>
                        </div>
                      </div>
                      {/* <div className="line-divider marg-10 dark"></div>
                      <div className="add-guest-family-wrapper text-m">
                        <div className="add-guest-family-field">
                          <label htmlFor="family_member_id">
                            Familjemedlem
                          </label>
                          <div className="add-guest-family-input-wrapper">
                            <input
                              className="on-transparent"
                              type="text"
                              id="family_member_id"
                              name="family_member_id"
                              disabled
                              placeholder="Medlemsnummer"
                              value={
                                addGuestData.family_member_id !== null
                                  ? addGuestData.family_member_id
                                  : ""
                              }
                              onChange={(e) =>
                                handleGuestInputChange(e, "family_member_id")
                              }
                            />
                            {addGuestData.family_member_id && (
                              <div
                              className="x"
                                onClick={() =>
                                  setAddGuestData({
                                    ...addGuestData,
                                    family_member_id: null,
                                  })
                                }
                              >
                                <img
                                  src={singleCross}
                                  alt="cross"
                                  className="cross-img pointer"
                                ></img>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="general-popup-form-field">
                          <button
                            type="button"
                            onClick={() => setShowChooseUserPopup(true)}
                          >
                            Välj familjemedlem
                          </button>
                        </div>
                      </div> */}
                    </div>
                  )}
                </div>
              </div>
            )}
        </form>
      </div>
      <HandleConfirmationPopup
        showPopup={showDeleteBookingPopup}
        onClose={() => {
          setCancelDueToRain(false);
          setShowDeleteBookingPopup(false);
        }}
        onConfirm={() => handleCancelBooking(booking)}
        questionText="Är du säker på att du vill avboka bokningen?"
        confirmText="Avboka"
        children={
          courtType === "OUTDOOR" && (
            <div className="child-flexer">
              <span className="text-m"> Avboka pga regn </span>
              <label className="toggle text-m">
                <input
                  className="text-m"
                  type="checkbox"
                  name="title"
                  checked={cancelDueToRain}
                  onChange={(e) => setCancelDueToRain(e.target.checked)}
                />
                <span className="slider round"></span>
              </label>
            </div>
          )
        }
        loading={loading}
      />

      <HandleConfirmationPopup
        showPopup={showMarkAsNoShowPopup}
        onClose={() => setShowMarkAsNoShowPopup(false)}
        onConfirm={handleMarkAsNoShow}
        questionText="Är du säker på att du vill markera bokningen som No show?"
        confirmText="Markera som 'No show'"
        denyText="Avbryt"
        loading={loading}
      />
      <ChooseUserPopup
        showPopup={showChooseUserPopup}
        onClose={() => {
          setShowChooseUserPopup(false);
        }}
        onConfirm={(user) => {
          setAddGuestData({
            ...addGuestData,
            family_member_id: user.member_id,
          });
          setShowChooseUserPopup(false);
        }}
      />
      {mainBookerData && (
        <EditCoPlayersPopup
          showPopup={showEditCoPlayersPopup}
          onClose={() => setShowEditCoPlayersPopup(false)}
          booking={bookingToLoop}
          onConfirm={(bookingConfirmResponse: IBooking) => {
            setUpdatedBookingCompare(bookingConfirmResponse);
            setShowEditCoPlayersPopup(false);
            props.onConfirm();
          }}
          cognitoUser={null}
          user={mainBookerData}
          coachesList={slot.coach_available}
          latestCoPlayers={[]}
          coaches={props.coaches}
          courts={props.courts}
          prices={props.prices}
          permissions={props.permissions}
          isAdmin={true}
          guestDataFromAdmin={guestsData}
        />
      )}

      {mainBookerData && (
        <EditMainBookerPopup
          showPopup={showEditMainBookerPopup}
          onClose={() => setShowEditMainBookerPopup(false)}
          booking={bookingToLoop}
          onConfirm={() => {
            setShowEditMainBookerPopup(false);
            props.onConfirm();
            handleClose();
          }}
        />
      )}

      <ConfirmationPopup
        showPopup={guestSlotChoice !== null}
        onClose={() => setGuestSlotChoice(null)}
        onConfirm={() => setGuestSlotChoice(null)}
        leftTopElement={<span className="text-m">Välj gästplats</span>}
      >
        <div className="general-popup-wrapper">
          <div className="general-popup-form">
            <div className="general-popup-form__header">
              <h2 className="text-l">
                En gästplats är redan betald. Vilken plats ska gästen{" "}
                {guestSlotChoice?.guestName} tilldelas?
              </h2>
            </div>
            <div className="guest-slot-choice-buttons">
              <button
                type="button"
                className="text-m"
                onClick={() => {
                  if (!guestSlotChoice) return;
                  assignGuestToSlot(
                    guestSlotChoice.guestId,
                    guestSlotChoice.paidIndex,
                    true,
                  );
                  setGuestSlotChoice(null);
                }}
              >
                Betald
              </button>
              <button
                type="button"
                className="text-m select-style"
                onClick={() => {
                  if (!guestSlotChoice) return;
                  assignGuestToSlot(
                    guestSlotChoice.guestId,
                    guestSlotChoice.unpaidIndex,
                    false,
                  );
                  setGuestSlotChoice(null);
                }}
              >
                Obetald
              </button>
            </div>
          </div>
        </div>
      </ConfirmationPopup>
    </Popup>
  );
}

export default AdminHandleBookingPopup;
